/** Turning a web2 skin into a real NFT.
 *
 *  Django does not mint. These endpoints take payment and queue the work; an
 *  external worker does the chain part and writes the result back, the same
 *  shape the pack delivery worker already runs in.
 */

const getCookie = (name: string): string | null => {
    for (const cookie of document.cookie.split(';')) {
        const [key, value] = cookie.trim().split('=')
        if (key === name) return decodeURIComponent(value)
    }
    return null
}

export type MintPrice = {
    price: string
    currency: string
    /** Off until there is a collection to mint into and a worker to mint with. */
    enabled: boolean
    /** Whether there is anywhere to send the asset. Buying from the store needs
     *  no wallet, so arriving at this offer without one is ordinary. */
    has_wallet: boolean
    /** Every wallet the player could receive it in, best guess first. */
    wallets: Array<{ pubkey: string; name: string; is_payout: boolean }>
    /** "devnet" while testing; empty on mainnet, where explorers need no cluster. */
    explorer_cluster?: string
}

export class SkinMintError extends Error {}

/** What converting costs right now.
 *
 *  Read rather than hardcoded: staff set it on the Skins page, so a number
 *  baked into the bundle would be whatever it was at build time.
 */
export const mintPrice = async (): Promise<MintPrice> => {
    const response = await fetch('/api/nft/skins/mint-price')
    if (!response.ok) throw new SkinMintError('Could not read the price.')
    return response.json()
}

/** Pay for, and queue, one skin's conversion.
 *
 *  Every refusal comes back as a 400 with a sentence -- no wallet, not enough
 *  balance, already converting -- and none of them has charged anything.
 */
export const requestMint = async (
    nftId: number,
    wallet?: string,
): Promise<{ status: string; fee_paid: string; wallet: string }> => {
    const response = await fetch(`/api/nft/skins/${nftId}/mint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') ?? '' },
        // The wallet the player picked. The server checks it is theirs: an
        // unchecked address would mint a paid-for asset into a stranger's
        // wallet, with no way back.
        body: JSON.stringify({ wallet }),
    })
    const body = await response.json().catch(() => null)
    if (!response.ok) {
        throw new SkinMintError(body?.error ?? `Could not start the conversion (${response.status}).`)
    }
    return body
}

export type MintState = {
    /** Still queued or being minted. */
    pending: boolean
    /** Pending | Processing | Minted | Failed | Refunded, or null if never asked for. */
    status: string | null
    mint?: string | null
    explorer_cluster?: string
    error?: string | null
    refunded?: boolean
}

/** How this skin's conversion is going.
 *
 *  Watched after payment so the panel can settle on what actually happened,
 *  rather than guessing from a timer. Reports the outcome as well as whether
 *  something is running, so a failure ends the wait instead of spinning on.
 */
export const mintStatus = async (nftId: number): Promise<MintState> => {
    const response = await fetch(`/api/nft/skins/${nftId}/mint-status`)
    if (!response.ok) return { pending: false, status: null }
    return response.json()
}


/** Where to look this asset up.
 *
 *  Solscan, with the cluster the server says it minted on -- hardcoding
 *  `devnet` would silently point at the wrong chain the day this goes to
 *  mainnet, and the link would 404 rather than obviously break.
 */
export const solscanUrl = (address: string, cluster?: string): string =>
    `https://solscan.io/token/${address}${cluster ? `?cluster=${cluster}` : ''}`
